import { translatableStringFactory } from "@@/shared/translations/translation";
import type { ZodTypeOf } from "@@/shared/zod-helpers";
import type { ZodSchema } from "zod";
import { z } from "zod";
import type { BrandedAs } from "./branded";
import { generateId } from "./generate-id";

const _generateIdPrefix = <const P extends `${string}__`>(prefix: P): P =>
    prefix.toLocaleLowerCase().replace(/\s+/g, "_") as typeof prefix;

const _factory =
    <T extends BrandedAs<string>>(prefix: `${string}__`) =>
    (): T =>
        generateId<T>({ prefix });

const _assert =
    <T extends BrandedAs<string>>(zodSchema: ZodSchema) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (id: any): id is T => {
        return zodSchema.safeParse(id).success;
    };

const _zodSchemaFactory = <T extends BrandedAs<string>>(
    regexMatch: RegExp,
    // prefixLength: number,
): ZodTypeOf<T> => {
    const schema = z
        .string({
            invalid_type_error: translatableStringFactory({
                sv: "ogiltigt id",
                en: "invalid id",
            }),
            required_error: translatableStringFactory({
                sv: "id krävs",
                en: "id required",
            }),
        })
        .regex(
            regexMatch,
            translatableStringFactory({
                sv: "ogiltigt id",
                en: "invalid id",
            }),
        ) as unknown as ZodTypeOf<T>;
    // )
    // .refine(
    //     id => id.length <= prefixLength,
    //     toTranslatableString({
    //         sv: "ogiltigt id",
    //         en: "invalid id",
    //     }),
    return schema;
};
const genericId = <
    T extends BrandedAs<string>,
    const P extends `${string}__` = `${string}__`,
>(
    idPrefix: P,
    options: {
        /** regex is only required if you've got old ids not conforming to standard: `${string}__`  */
        regexMatch?: RegExp;
    } = {},
): {
    readonly zodSchema: ZodTypeOf<T>;
    readonly factory: () => T;
    readonly assert: (id: unknown) => id is T;
    readonly prefix: P;
} => {
    const prefix = _generateIdPrefix<P>(idPrefix);
    const schemaRegex = options.regexMatch ?? new RegExp(`^${prefix}.*$`);
    const zodSchema = _zodSchemaFactory<T>(schemaRegex);

    return {
        zodSchema,
        factory: _factory<T>(prefix),
        assert: _assert<T>(zodSchema),
        prefix,
    } as const;
};

export { genericId };
